/**
 * statusBar.js — thin wrapper over `@capacitor/status-bar`.
 *
 * The plugin is only meaningful on iOS / Android; on web all calls are
 * safe no-ops. Consumers should call `applyTheme()` on theme change
 * (see `src/theme/theme.jsx`) and again on app resume so background/
 * foreground transitions don't leave the native status bar with a
 * stale style (a common iOS quirk).
 *
 * The GreenCharge brand uses a very dark canvas in dark mode
 * (`--gc-bg: #05070c`) and a bright canvas in light mode. Status-bar
 * text colour is the inverse of the canvas — dark text on light, light
 * text on dark — so the plugin's `Style.Dark` / `Style.Light` names
 * are literally "dark-content" and "light-content" and read backwards
 * from what you'd expect.
 */
import { isNative, isAndroid } from './platform.js';

let _statusBar = null;
let _StyleEnum = null;

async function statusBar() {
  if (_statusBar) return _statusBar;
  const mod = await import('@capacitor/status-bar');
  _statusBar = mod.StatusBar;
  _StyleEnum = mod.Style;
  return _statusBar;
}

/**
 * Apply the status-bar treatment that matches the current theme.
 * `theme` is `'light' | 'dark'`. Extra args (e.g. `overlays`) are
 * intentionally not exposed — the shell handles safe-area padding via
 * CSS envs, so the native status bar should always be opaque and NOT
 * overlaid on top of the WebView (`overlaysWebView: false` — set in
 * capacitor.config.ts).
 */
export async function applyTheme(theme) {
  if (!isNative()) return;
  try {
    const bar = await statusBar();
    const isDark = theme === 'dark';
    await bar.setStyle({ style: isDark ? _StyleEnum.Light : _StyleEnum.Dark });
    if (isAndroid()) {
      // GreenCharge canvas colours — must match the CSS `--gc-bg` token
      // for the transition between native status bar and web body to be
      // seamless. If tokens.less changes these, update them here too.
      await bar.setBackgroundColor({ color: isDark ? '#05070c' : '#f6f8fb' });
    }
  } catch { /* plugin missing / not on this platform */ }
}

/** Hide the status bar (used sparingly — for example during QR scanning). */
export async function hide() {
  if (!isNative()) return;
  try { const bar = await statusBar(); await bar.hide(); } catch { /* ignore */ }
}

/** Restore the status bar after a call to `hide()`. */
export async function show() {
  if (!isNative()) return;
  try { const bar = await statusBar(); await bar.show(); } catch { /* ignore */ }
}
